import { createClient } from '@supabase/supabase-js';

// Browser-side Supabase client (singleton)
let browserClient = null;

// Get environment variables
const getEnvVars = () => {
  const rawUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const rawKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
  
  return {
    supabaseUrl: rawUrl ? String(rawUrl)?.trim()?.replace(/^["']|["']$/g, '') : null,
    supabaseAnonKey: rawKey ? String(rawKey)?.trim()?.replace(/^["']|["']$/g, '') : null
  };
};

// Create browser client with proper configuration
const createBrowserClient = () => {
  const { supabaseUrl, supabaseAnonKey } = getEnvVars();

  if (!supabaseUrl || !supabaseAnonKey) {
    console.error(
      '\n' + '═══════════════════════════════════════════════════════════════\n'+ '❌ SUPABASE CONFIGURATION REQUIRED\n'+ '═══════════════════════════════════════════════════════════════\n'+ '\n'+ 'Your environment variables are not configured.\n'+ '\n'+ '📋 STEP-BY-STEP FIX:\n'+ '\n'+ '1. Create/Open ".env.local" file in your project root\n'+ '   (This should be in the same folder as package.json)\n'+ '\n'+ '2. Add these two lines:\n'+ '   NEXT_PUBLIC_SUPABASE_URL=https://your-project-id.supabase.co\n' +
      '   NEXT_PUBLIC_SUPABASE_ANON_KEY=your-anon-key-here\n'+ '\n'+ '3. Get your actual credentials from:\n'+ '   👉 https://app.supabase.com/project/_/settings/api\n' +
      '\n'+ '4. Replace "your-project-id" and "your-anon-key-here" with\n'+ '   your actual Supabase project URL and anon/public key\n'+ '\n'+ '5. IMPORTANT: Restart your dev server completely:\n'+ '   - Stop: Press Ctrl+C (or Cmd+C on Mac)\n'+ '   - Start: Run "npm run dev" again\n'+ '\n'+ '6. Hard refresh your browser:\n'+ '   - Windows/Linux: Ctrl+Shift+R\n'+ '   - Mac: Cmd+Shift+R\n'+ '\n'+ '⚠️ Note: This file (supabase.js) is DEPRECATED\n'+ 'Consider migrating to src/lib/supabase/client.jsx\n'+ '═══════════════════════════════════════════════════════════════\n'
    );
    return null;
  }

  // CRITICAL: Comprehensive placeholder detection
  const placeholderPatterns = [
    'your_supabase_url',
    'your-supabase-url',
    'your_project',
    'your-project',
    'xxxxx',
    'placeholder',
    'example',
    'localhost'
  ];
  
  const keyPlaceholderPatterns = [
    'your_anon_key',
    'your-anon-key',
    'your_key',
    'your-key',
    'anon-key-here',
    'placeholder',
    'example'
  ];

  const hasUrlPlaceholder = placeholderPatterns?.some(pattern => 
    supabaseUrl?.toLowerCase()?.includes(pattern)
  );
  
  const hasKeyPlaceholder = keyPlaceholderPatterns?.some(pattern => 
    supabaseAnonKey?.toLowerCase()?.includes(pattern)
  );

  if (hasUrlPlaceholder || hasKeyPlaceholder) {
    console.error(
      '\n' + '═══════════════════════════════════════════════════════════════\n'+ '❌ PLACEHOLDER VALUES DETECTED\n'+ '═══════════════════════════════════════════════════════════════\n'+ 'Your .env.local file contains placeholder text instead of\n'+ 'actual Supabase credentials.\n'+ '\n' +
      `Current URL: "${supabaseUrl}"\n` +
      '\n' + '📋 TO FIX:\n'+ '\n'+ '1. Go to: https://app.supabase.com/project/_/settings/api\n' +
      '\n'+ '2. Copy your ACTUAL project URL (looks like):\n'+ '   https://abcdefghijklmnop.supabase.co\n' +
      '\n'+ '3. Copy your ACTUAL anon/public key (long string)\n'+ '\n'+ '4. Update .env.local with REAL values:\n'+ '   NEXT_PUBLIC_SUPABASE_URL=https://abcdefghijklmnop.supabase.co\n' +
      '   NEXT_PUBLIC_SUPABASE_ANON_KEY=eyJhb...[your actual key]\n'+ '\n'+ '5. Restart dev server (Ctrl+C, then npm run dev)\n'+ '\n'+ '6. Hard refresh browser (Ctrl+Shift+R)\n'+ '═══════════════════════════════════════════════════════════════\n'
    );
    return null;
  }

  // Validate URL format
  const urlPattern = /^https?:\/\/.+\.supabase\.co\/?$/i;
  if (!urlPattern?.test(supabaseUrl)) {
    console.error(
      '\n' + '═══════════════════════════════════════════════════════════════\n'+ '❌ INVALID SUPABASE URL FORMAT\n'+ '═══════════════════════════════════════════════════════════════\n'+ '\n' +
      `Current value: "${supabaseUrl}"\n` +
      '\n' + 'Expected format: https://your-project-id.supabase.co\n' +
      '\n'+ '📋 REQUIREMENTS:\n'+ '  ✓ Must start with https://\n' +
      '  ✓ Must end with .supabase.co\n'+ '  ✓ Should look like: https://abcdefghijklmnop.supabase.co\n' +
      '\n'+ '📋 TO FIX:\n'+ '1. Open .env.local file\n'+ '2. Get correct URL from: https://app.supabase.com/project/_/settings/api\n' +
      '3. Copy the full "Project URL" exactly as shown\n'+ '4. Update NEXT_PUBLIC_SUPABASE_URL with the correct value\n'+ '5. Save file and restart dev server\n'+ '═══════════════════════════════════════════════════════════════\n'
    );
    return null;
  }

  if (!browserClient) {
    try {
      browserClient = createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
          autoRefreshToken: true,
          persistSession: true,
          detectSessionInUrl: true,
          storage: typeof window !== 'undefined' ? window.localStorage : undefined
        }
      });
      
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ Legacy Supabase client initialized\n'+ 'MIGRATE TO: src/lib/supabase/client.jsx for better Next.js 14+ support');
      }
    } catch (error) {
      console.error(`❌ [Legacy] Supabase SDK Error: ${error?.message}`);
      return null;
    }
  }

  return browserClient;
};

// Create server client (new instance each time)
export const createServerClient = () => {
  const { supabaseUrl, supabaseAnonKey } = getEnvVars();

  if (!supabaseUrl || !supabaseAnonKey) {
    console.error(
      '\n' + '❌ [Server] Supabase configuration missing\n'+ 'Configure .env.local with your Supabase credentials\n'
    );
    return null;
  }

  // CRITICAL: Comprehensive placeholder detection
  const placeholderPatterns = [
    'your_supabase_url',
    'your-supabase-url',
    'your_project',
    'your-project',
    'xxxxx',
    'placeholder',
    'example',
    'localhost'
  ];
  
  const keyPlaceholderPatterns = [
    'your_anon_key',
    'your-anon-key',
    'your_key',
    'your-key',
    'anon-key-here',
    'placeholder',
    'example'
  ];

  const hasUrlPlaceholder = placeholderPatterns?.some(pattern => 
    supabaseUrl?.toLowerCase()?.includes(pattern)
  );
  
  const hasKeyPlaceholder = keyPlaceholderPatterns?.some(pattern => 
    supabaseAnonKey?.toLowerCase()?.includes(pattern)
  );

  if (hasUrlPlaceholder || hasKeyPlaceholder) {
    console.error(
      '\n' +
      `❌ [Server] Placeholder values detected: "${supabaseUrl}"\n` +
      'Replace with actual Supabase credentials from:\n' + 'https://app.supabase.com/project/_/settings/api\n'
    );
    return null;
  }

  // Validate URL format
  const urlPattern = /^https?:\/\/.+\.supabase\.co\/?$/i;
  if (!urlPattern?.test(supabaseUrl)) {
    console.error(
      '\n' +
      `❌ [Server] Invalid Supabase URL format: "${supabaseUrl}"\n` +
      'Expected: https://your-project-id.supabase.co\n'
    );
    return null;
  }

  try {
    return createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: false,
        persistSession: false,
        detectSessionInUrl: false
      }
    });
  } catch (error) {
    console.error(`❌ [Legacy Server] Supabase SDK Error: ${error?.message}`);
    return null;
  }
};

// Main client getter - works in both browser and server contexts
export const getSupabaseClient = () => {
  // Server-side: create new instance
  if (typeof window === 'undefined') {
    return createServerClient();
  }
  
  // Browser-side: return singleton
  return createBrowserClient();
};

// Direct legacy client initialization
let _legacyClient = null;

const getLegacyClient = () => {
  if (!_legacyClient) {
    _legacyClient = getSupabaseClient();
  }
  return _legacyClient;
};

// Legacy export for backwards compatibility
export const supabase = {
  get auth() { return getLegacyClient()?.auth; },
  get from() { return getLegacyClient()?.from; },
  get storage() { return getLegacyClient()?.storage; },
  get functions() { return getLegacyClient()?.functions; },
  get realtime() { return getLegacyClient()?.realtime; },
  get channel() { return getLegacyClient()?.channel; },
  get rpc() { return getLegacyClient()?.rpc; }
};

// Default export
export default getSupabaseClient;